import { CONTRACT_SCHEMA_VERSION } from "../domain/common";
import type { BoardContextImport, DataImportResult, StoredBoardContext } from "../domain/types";
import { verifyPayloadHash } from "../contracts/canonical-json";
import { validateContract } from "../contracts/validator";
import { createAuditEvent } from "../db/audit";
import type { CEOOSDatabase } from "../db/database";
import { RuleErrorCode } from "../rules/errors";
import { detectBoardImportConflicts } from "../rules/local-rules";

export interface BoardImportPreflight {
  envelope?: BoardContextImport;
  result: DataImportResult;
  sourceFile: StoredBoardContext["sourceFile"];
}

export async function preflightBoardContextImport(
  database: CEOOSDatabase,
  value: unknown,
  sourceFile: StoredBoardContext["sourceFile"],
  now = new Date().toISOString(),
): Promise<BoardImportPreflight> {
  const validation = validateContract<BoardContextImport>(value, "board-context-import");
  if (!validation.ok) {
    return {
      sourceFile,
      result: {
        id: crypto.randomUUID(),
        schemaVersion: CONTRACT_SCHEMA_VERSION,
        importedAt: now,
        schemaName: "board-context-import",
        status: "rejected",
        sourceFileName: sourceFile.name,
        added: 0,
        skipped: 0,
        conflicts: [],
        errors: validation.errors,
      },
    };
  }
  if (!(await verifyPayloadHash(validation.value))) {
    return {
      sourceFile,
      result: {
        id: crypto.randomUUID(),
        schemaVersion: CONTRACT_SCHEMA_VERSION,
        importedAt: now,
        schemaName: validation.value.schema_name,
        status: "rejected",
        sourceFileName: sourceFile.name,
        added: 0,
        skipped: 0,
        conflicts: [],
        errors: [
          {
            code: RuleErrorCode.CONTRACT_HASH_MISMATCH,
            path: "/payload_hash",
            message: "payload_hash 与规范化 payload 不一致。",
          },
        ],
      },
    };
  }
  const existing = await database.boardImports.toArray();
  const result = detectBoardImportConflicts(validation.value, existing, now);
  return {
    envelope: validation.value,
    sourceFile,
    result: {
      ...result,
      sourceFileName: sourceFile.name,
    },
  };
}

export async function commitBoardContextImport(
  database: CEOOSDatabase,
  preflight: BoardImportPreflight,
  confirmConflict = false,
): Promise<DataImportResult> {
  if (!preflight.envelope || preflight.result.status === "rejected") {
    return preflight.result;
  }
  if (preflight.result.conflicts.some((item) => item.reason === "duplicate")) {
    await database.importResults.put(preflight.result);
    return preflight.result;
  }
  if (preflight.result.status === "needs_confirmation" && !confirmConflict) {
    await database.importResults.put(preflight.result);
    return preflight.result;
  }

  const envelope = preflight.envelope;
  const stored: StoredBoardContext = {
    id: crypto.randomUUID(),
    contractId: envelope.contract_id,
    schemaVersion: envelope.schema_version,
    date: envelope.payload.date,
    importedAt: preflight.result.importedAt,
    payloadHash: envelope.payload_hash,
    status: preflight.result.conflicts.length > 0 ? "conflict" : "active",
    sourceFile: preflight.sourceFile,
    envelope,
  };
  const committed: DataImportResult = {
    ...preflight.result,
    status: "committed",
    added: 1,
  };

  await database.transaction(
    "rw",
    database.boardImports,
    database.importResults,
    database.auditEvents,
    async () => {
      const sameDateActive = await database.boardImports
        .where("[date+status]")
        .equals([envelope.payload.date, "active"])
        .toArray();
      for (const existing of sameDateActive) {
        await database.boardImports.put({ ...existing, status: "historical" });
      }
      await database.boardImports.put(stored);
      await database.importResults.put(committed);
      await database.auditEvents.put(
        createAuditEvent({
          entityType: "BoardContextImport",
          entityId: stored.id,
          action: "import",
          actor: "user",
          reason: "用户确认导入只读 BoardContext；原始合同保留历史。",
          now: committed.importedAt,
        }),
      );
    },
  );
  return committed;
}
